import type {AuditAdjustHeader, AuditAdjustLine} from '@/services/auditService/interface/adjust-orchestrate';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import type {CommonRow} from '@/services/auditService/interface/wp-row';
import {getAdjustmentTotal} from '@/adjustment/adjust/utils/adjustmentLockUtils';
import {
    getOriginalEntryFromFsCategory,
    resolveFromPaperAdjustmentFields,
    resolveToPaperFieldsFromPairing,
} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {computeStoredAdjustmentFromUserInput} from '@/adjustment/adjust/utils/classifyRows';
import {computeWpRowCurrentFinal} from '@/utils/wpPriorPeriodBfRollForward';

export const TAX_CF_SOURCE_ROW_COUNT = 7;

const PL_TAX_CODE = 'PL-TAX';

export const TAX_SALE_FS_CODES = new Set<string>(['BS-CA-TAXR', 'BS-CL-TAXP', PL_TAX_CODE]);

function collectAdjustmentFsCodes(state: {
    header?: {lines?: Array<{code?: string; scheduleCode?: string}>} | null;
}): Set<string> {
    const codes = new Set<string>();
    for (const line of state.header?.lines || []) {
        const code = String(line.code || (line as {scheduleCode?: string}).scheduleCode || '').trim().toUpperCase();
        if (code) codes.add(code);
    }
    return codes;
}

/** getAdjustmentWithWorkingPapers 回显的 ADJUST_SALE 编辑态（仅 Tax Sale 模块） */
export function isTaxSaleBackendEditState(state: {
    draftHydratedFromBackend?: boolean;
    header?: {adjustmentMode?: number; lines?: Array<{code?: string; scheduleCode?: string}>} | null;
}): boolean {
    if (!state.draftHydratedFromBackend) return false;
    if (state.header?.adjustmentMode !== AdjustmentMode.ADJUST_SALE) return false;

    const lineCodes = collectAdjustmentFsCodes(state);
    if (!lineCodes.has(PL_TAX_CODE)) return false;
    if (!lineCodes.has('BS-CA-TAXR') && !lineCodes.has('BS-CL-TAXP')) return false;
    for (const code of lineCodes) {
        if (!TAX_SALE_FS_CODES.has(code)) return false;
    }
    return true;
}

export function getTaxSaleRenderRowKey(item: {
    code: string;
    arrayName: string;
    rowIndex: number;
    isSynthetic?: boolean;
}): string {
    const codeUpper = String(item.code || '').trim().toUpperCase();
    if (item.isSynthetic) {
        return `${codeUpper}:_synthetic:0`;
    }
    return `${codeUpper}:${item.arrayName}:${item.rowIndex}`;
}

/** Tax Sale 编辑：锁定列内仅允许正数输入，按行规则计算存储 adjustment */
export function computeTaxSalePositiveOnlyAdjustment(
    item: {
        code: string;
        arrayName: string;
        rowIndex: number;
        row?: CommonRow;
        isSynthetic?: boolean;
        originalEntry?: string;
    },
    lockedPosition: 'Dr' | 'Cr',
    positiveUserVal: number,
): number {
    const absVal = Math.abs(Number(positiveUserVal) || 0);
    if (absVal < 0.001) return 0;

    const codeUpper = String(item.code || '').trim().toUpperCase();
    if (codeUpper === PL_TAX_CODE && item.arrayName === 'commonRow' && item.row) {
        const logicalIdx = taxLogicalRowIndex(item.row, item.rowIndex);
        return resolveTaxAdjustmentSign(logicalIdx, item.row, absVal);
    }

    const originalEntry = item.originalEntry || getOriginalEntryFromFsCategory(codeUpper);
    return computeStoredAdjustmentFromUserInput(originalEntry, lockedPosition, absVal);
}

/** Over-provided in prior years: adjustment negative only; Under-provided: positive only. */
export function resolveTaxAdjustmentSign(rowIndex: number, row: CommonRow, value: number): number {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return 0;
    const item = String(row.item || '').trim().toLowerCase();
    const negativeOnly = rowIndex === TAX_OVER_PROVIDED_ROW_INDEX
        || (item.includes('over-provided') && item.includes('prior'));
    const positiveOnly = rowIndex === TAX_UNDER_PROVIDED_ROW_INDEX
        || (item.includes('under-provided') && item.includes('prior'));
    if (negativeOnly) return n > 0 ? -Math.abs(n) : n;
    if (positiveOnly) return n < 0 ? Math.abs(n) : n;
    return n;
}

const TAX_REFUNDABLE_BF_ROW_INDEX = 0;
const TAX_PAYABLE_BF_ROW_INDEX = 1;
const TAX_REFUNDED_ROW_INDEX = 3;

function isTaxBfLogicalIndex(logicalIndex: number): boolean {
    return logicalIndex === TAX_REFUNDABLE_BF_ROW_INDEX || logicalIndex === TAX_PAYABLE_BF_ROW_INDEX;
}

function isTaxPriorYearsLogicalIndex(logicalIndex: number): boolean {
    return logicalIndex === TAX_OVER_PROVIDED_ROW_INDEX || logicalIndex === TAX_UNDER_PROVIDED_ROW_INDEX;
}

function hasTaxPriorYearsSaleBackendAdjustment(row: CommonRow | undefined): boolean {
    if (!row || !Array.isArray(row.adjustments) || row.adjustments.length === 0) {
        return false;
    }
    return row.adjustments.some((detail) =>
        Number(detail?.adjustmentMode) === AdjustmentMode.ADJUST_SALE
        && Math.abs(Number(detail?.adjustment) || 0) > 0.001,
    );
}

function clearTaxBfAdjustmentFields(row: CommonRow): CommonRow {
    const {
        adjustments: _adjustments,
        adjustHeaderId: _adjustHeaderId,
        ...rest
    } = row as CommonRow & {adjustHeaderId?: string};
    return {...rest, adjustment: 0};
}

/**
 * Tax (refundable)/(payable) b/f：不渲染 adjustmentMode=5 (ADJUST_SALE) companion；
 * 其余 mode 正常保留渲染。
 */
function stripTaxBfSaleCompanionAdjustments(row: CommonRow): CommonRow {
    if (!Array.isArray(row.adjustments) || row.adjustments.length === 0) {
        return row;
    }

    const remaining = row.adjustments.filter(
        (detail) => Number(detail?.adjustmentMode) !== AdjustmentMode.ADJUST_SALE,
    );
    if (remaining.length === row.adjustments.length) {
        return row;
    }
    if (remaining.length > 0) {
        return {...row, adjustments: remaining, adjustment: 0};
    }
    return clearTaxBfAdjustmentFields(row);
}

/** After backend load: hide ADJUST_SALE on b/f rows; keep non-sale adjustments for display. */
export function sanitizeTaxRowsAfterBackendLoad(rows: CommonRow[]): CommonRow[] {
    if (rows.length === 0) return rows;

    return rows.map((row, idx) => {
        const logicalIdx = taxLogicalRowIndex(row, idx);
        if (!isTaxBfLogicalIndex(logicalIdx)) return row;
        return stripTaxBfSaleCompanionAdjustments(row);
    });
}

function readTaxPriorYearsSaleInput(
    row: CommonRow | undefined,
    logicalIndex: number,
    pendingValue: number | undefined,
    isRowLocked: (row: CommonRow) => boolean,
): number {
    if (!row?.id) return 0;
    if (pendingValue !== undefined) {
        return Number(pendingValue) || 0;
    }
    if (isTaxPriorYearsLogicalIndex(logicalIndex) && isRowLocked(row)) {
        return getAdjustmentTotal(row);
    }
    if (isRowLocked(row)) return 0;
    return Number(row.adjustment) || 0;
}

export const TAX_EXPENSES_ROW_INDEX = 4;
export const TAX_OVER_PROVIDED_ROW_INDEX = 5;
export const TAX_UNDER_PROVIDED_ROW_INDEX = 6;
export const TAX_CF_ROW_INDEX = 7;

/** Marker key for companion tax line: points to the WP row whose adjustment is persisted as line attr. */
export const TAX_SALE_ATTR_ROW_REF_KEY = 'saleAttrRowRef';
export const TAX_REFUNDABLE_BF_ITEM = 'Tax (refundable) b/f';
export const TAX_PAYABLE_BF_ITEM = 'Tax payable b/f';
export const TAX_COMMON_ROW_CLASS = 'commonRow';

export type TaxSaleCompanionCode = 'BS-CL-TAXP' | 'BS-CA-TAXR';

export type TaxPriorYearsSaleMode = 'over-provided' | 'under-provided';

/** Unsaved PL-TAX rows use positional id 0–6; persisted rows use DB ids (>= 10). */
const TAX_POSITIONAL_ID_MAX = 10;

function resolveTaxLogicalIndexByItem(row: CommonRow): number {
    const item = String(row.item || '').trim().toLowerCase();
    if (item.startsWith('tax (refundable) b/f')) return 0;
    if (item.startsWith('tax payable b/f')) return 1;
    if (item.startsWith('tax (paid) during')) return 2;
    if (item.startsWith('tax refunded during')) return 3;
    if (item.startsWith('tax expenses during')) return 4;
    if (item.includes('over-provided') && item.includes('prior')) return TAX_OVER_PROVIDED_ROW_INDEX;
    if (item.includes('under-provided') && item.includes('prior')) return TAX_UNDER_PROVIDED_ROW_INDEX;
    if (item.startsWith('tax payable c/f') || item.startsWith('tax (refundable) c/f')) return 7;
    return -1;
}

export function taxLogicalRowIndex(row: CommonRow, fallbackIndex: number): number {
    if (fallbackIndex >= 0) return fallbackIndex;
    if (typeof row.id === 'number' && row.id >= 0 && row.id < TAX_POSITIONAL_ID_MAX) {
        return row.id;
    }
    const byItem = resolveTaxLogicalIndexByItem(row);
    return byItem >= 0 ? byItem : fallbackIndex;
}

export function findTaxRowByLogicalIndex(rows: CommonRow[], logicalIndex: number): CommonRow | undefined {
    return rows.find((row, idx) => taxLogicalRowIndex(row, idx) === logicalIndex);
}

function getRowByLogicalIndex(rows: CommonRow[], logicalIndex: number): CommonRow | undefined {
    return findTaxRowByLogicalIndex(rows, logicalIndex);
}

/** Sale input: pending from "+ add new" flow, or direct row.adjustment when row is not locked. */
function getRowSaleAdjustmentInput(
    row: CommonRow | undefined,
    pendingAdjustments: Record<number, number>,
    isRowLocked: (row: CommonRow) => boolean,
): number {
    if (!row || row.id == null) return 0;
    if (pendingAdjustments[row.id] !== undefined) {
        return Number(pendingAdjustments[row.id]) || 0;
    }
    if (isRowLocked(row)) return 0;
    return Number(row.adjustment) || 0;
}

function isPriorYearsSaleAdjustmentActive(
    rows: CommonRow[],
    pendingAdjustments: Record<number, number>,
    logicalIndex: number,
    isRowLocked: (row: CommonRow) => boolean,
): boolean {
    const row = getRowByLogicalIndex(rows, logicalIndex);
    if (!row || row.id == null) return false;
    if (Math.abs(Number(pendingAdjustments[row.id]) || 0) > 0.001) {
        return true;
    }
    if (isRowLocked(row)) {
        return hasTaxPriorYearsSaleBackendAdjustment(row);
    }
    return Math.abs(getRowSaleAdjustmentInput(row, pendingAdjustments, isRowLocked)) > 0.001;
}

export function getOverProvidedAdjustmentTotal(
    rows: CommonRow[],
    pendingAdjustments: Record<number, number>,
    isRowLocked: (row: CommonRow) => boolean = () => false,
): number {
    return getRowSaleAdjustmentInput(
        getRowByLogicalIndex(rows, TAX_OVER_PROVIDED_ROW_INDEX),
        pendingAdjustments,
        isRowLocked,
    );
}

export function getUnderProvidedAdjustmentTotal(
    rows: CommonRow[],
    pendingAdjustments: Record<number, number>,
    isRowLocked: (row: CommonRow) => boolean = () => false,
): number {
    return getRowSaleAdjustmentInput(
        getRowByLogicalIndex(rows, TAX_UNDER_PROVIDED_ROW_INDEX),
        pendingAdjustments,
        isRowLocked,
    );
}

export function isOverProvidedSaleAdjustmentActive(
    rows: CommonRow[],
    pendingAdjustments: Record<number, number>,
    isRowLocked: (row: CommonRow) => boolean,
): boolean {
    return isPriorYearsSaleAdjustmentActive(
        rows,
        pendingAdjustments,
        TAX_OVER_PROVIDED_ROW_INDEX,
        isRowLocked,
    );
}

export function isUnderProvidedSaleAdjustmentActive(
    rows: CommonRow[],
    pendingAdjustments: Record<number, number>,
    isRowLocked: (row: CommonRow) => boolean,
): boolean {
    return isPriorYearsSaleAdjustmentActive(
        rows,
        pendingAdjustments,
        TAX_UNDER_PROVIDED_ROW_INDEX,
        isRowLocked,
    );
}

export function getTaxPriorYearsSaleMode(
    rows: CommonRow[],
    pendingAdjustments: Record<number, number>,
    isRowLocked: (row: CommonRow) => boolean,
): TaxPriorYearsSaleMode | null {
    const overActive = isOverProvidedSaleAdjustmentActive(rows, pendingAdjustments, isRowLocked);
    const underActive = isUnderProvidedSaleAdjustmentActive(rows, pendingAdjustments, isRowLocked);
    if (overActive && !underActive) return 'over-provided';
    if (underActive && !overActive) return 'under-provided';
    if (overActive && underActive) {
        const overRow = getRowByLogicalIndex(rows, TAX_OVER_PROVIDED_ROW_INDEX);
        const underRow = getRowByLogicalIndex(rows, TAX_UNDER_PROVIDED_ROW_INDEX);
        const overPending = overRow?.id != null ? Math.abs(pendingAdjustments[overRow.id] ?? 0) : 0;
        const underPending = underRow?.id != null ? Math.abs(pendingAdjustments[underRow.id] ?? 0) : 0;
        return underPending >= overPending ? 'under-provided' : 'over-provided';
    }
    return null;
}

export function computeTaxSalePendingState(params: {
    rows: CommonRow[];
    commonRow: CommonRow[];
    tablePending: Record<number, number>;
    isRowLocked: (row: CommonRow) => boolean;
    resolveAdjustmentSign: (rowIndex: number, row: CommonRow, value: number) => number;
    clearPayableMirror?: boolean;
    /** When false, do not mirror prior-years input onto b/f rows (TaxWp independent editing). */
    applyBfMirror?: boolean;
}): Record<number, number> {
    const {
        rows,
        commonRow,
        tablePending,
        isRowLocked,
        resolveAdjustmentSign,
        clearPayableMirror = false,
        applyBfMirror = true,
    } = params;

    const normalized: Record<number, number> = {};

    for (const [rowIdStr, val] of Object.entries(tablePending)) {
        const rowId = Number(rowIdStr);
        const rowIdx = commonRow.findIndex((r) => r.id === rowId);
        const row = rowIdx >= 0 ? commonRow[rowIdx] : undefined;
        if (row) {
            normalized[rowId] = resolveAdjustmentSign(taxLogicalRowIndex(row, rowIdx), row, val);
        } else {
            normalized[rowId] = val;
        }
    }

    const overProvidedRow = findTaxRowByLogicalIndex(rows, TAX_OVER_PROVIDED_ROW_INDEX);
    const underProvidedRow = findTaxRowByLogicalIndex(rows, TAX_UNDER_PROVIDED_ROW_INDEX);
    const payableRow = findTaxRowByLogicalIndex(rows, TAX_PAYABLE_BF_ROW_INDEX);
    const refundableRow = findTaxRowByLogicalIndex(rows, TAX_REFUNDABLE_BF_ROW_INDEX);

    const overInput = readTaxPriorYearsSaleInput(
        overProvidedRow,
        TAX_OVER_PROVIDED_ROW_INDEX,
        overProvidedRow?.id != null ? normalized[overProvidedRow.id] : undefined,
        isRowLocked,
    );
    const underInput = readTaxPriorYearsSaleInput(
        underProvidedRow,
        TAX_UNDER_PROVIDED_ROW_INDEX,
        underProvidedRow?.id != null ? normalized[underProvidedRow.id] : undefined,
        isRowLocked,
    );
    const hasOverPending = Math.abs(overInput) > 0.001;
    const hasUnderPending = Math.abs(underInput) > 0.001;
    const isSalePendingInput = hasOverPending || hasUnderPending;

    if (applyBfMirror && isSalePendingInput) {
        if (hasUnderPending && refundableRow?.id != null && tablePending[refundableRow.id] === undefined) {
            normalized[refundableRow.id] = resolveAdjustmentSign(
                TAX_REFUNDABLE_BF_ROW_INDEX,
                refundableRow,
                -underInput,
            );
        } else if (hasOverPending && payableRow?.id != null && tablePending[payableRow.id] === undefined) {
            normalized[payableRow.id] = resolveAdjustmentSign(
                TAX_PAYABLE_BF_ROW_INDEX,
                payableRow,
                -overInput,
            );
        }
    } else if (clearPayableMirror) {
        if (payableRow?.id != null && tablePending[payableRow.id] === undefined) {
            delete normalized[payableRow.id];
        }
        if (refundableRow?.id != null && tablePending[refundableRow.id] === undefined) {
            delete normalized[refundableRow.id];
        }
    }

    return normalized;
}

function buildCommonRowSnapshot(
    rows: CommonRow[],
    mode: TaxPriorYearsSaleMode,
    sourceAdj: number,
    mirrorAdj: number,
): Record<string, unknown>[] {
    return rows.map((row, idx) => {
        const logicalIdx = taxLogicalRowIndex(row, idx);
        const {
            adjustments: _adjustments,
            adjustHeaderId: _adjustHeaderId,
            ...rest
        } = row as CommonRow & {adjustHeaderId?: string};

        let adjustment: number | null | undefined;
        if (mode === 'over-provided' && logicalIdx === TAX_OVER_PROVIDED_ROW_INDEX) {
            adjustment = sourceAdj;
        } else if (mode === 'under-provided' && logicalIdx === TAX_UNDER_PROVIDED_ROW_INDEX) {
            adjustment = sourceAdj;
        } else if (mode === 'over-provided' && logicalIdx === TAX_PAYABLE_BF_ROW_INDEX) {
            adjustment = mirrorAdj;
        } else if (mode === 'under-provided' && logicalIdx === TAX_REFUNDABLE_BF_ROW_INDEX) {
            adjustment = mirrorAdj;
        } else if (logicalIdx === TAX_REFUNDED_ROW_INDEX) {
            adjustment = 0;
        } else {
            adjustment = null;
        }

        return {
            ...rest,
            annualId: rest.annualId ?? null,
            code: rest.code ?? null,
            autoAdj: rest.autoAdj ?? null,
            appendA: rest.appendA ?? null,
            appendB: rest.appendB ?? null,
            addBackPercent: rest.addBackPercent ?? null,
            adjustment,
        };
    });
}

export function buildTaxPriorYearsSaleHeader(params: {
    companyId: number;
    annualId: number;
    currency: string;
    preparedUserId: number;
    rows: CommonRow[];
    mode: TaxPriorYearsSaleMode;
    sourceAdj: number;
    plTaxFsId: number;
    plTaxOriginalEntry?: string | null;
    companionFsId: number;
    companionCode: TaxSaleCompanionCode;
    remark?: string;
}): AuditAdjustHeader {
    const {
        companyId,
        annualId,
        currency,
        preparedUserId,
        rows,
        mode,
        sourceAdj,
        plTaxFsId,
        plTaxOriginalEntry,
        companionFsId,
        companionCode,
        remark = 'To reclassify the accounts',
    } = params;

    const isUnderProvided = mode === 'under-provided';
    const mirrorRowIndex = isUnderProvided ? TAX_REFUNDABLE_BF_ROW_INDEX : TAX_PAYABLE_BF_ROW_INDEX;
    const mirrorItem = isUnderProvided ? TAX_REFUNDABLE_BF_ITEM : TAX_PAYABLE_BF_ITEM;
    const mirrorRow = getRowByLogicalIndex(rows, mirrorRowIndex);

    const absAmount = Math.abs(sourceAdj);
    const plTaxFields = resolveFromPaperAdjustmentFields(
        plTaxOriginalEntry || 'Dr',
        sourceAdj,
        'PL-TAX',
    );
    const companionFields = resolveToPaperFieldsFromPairing('PL-TAX', sourceAdj, companionCode);
    const mirrorAdj = companionFields.adjustment;

    return {
        companyId,
        annualId,
        adjustmentMode: AdjustmentMode.ADJUST_SALE,
        refNo: '',
        status: 0,
        preparedUserId,
        preparedDate: new Date().toISOString(),
        reviewedUserId: 0,
        reviewedDate: '',
        remark,
        lines: [
            {
                fsId: plTaxFsId,
                code: 'PL-TAX',
                currency,
                adjustmentAmount: absAmount,
                entry: plTaxFields.entry,
                details: [{commonRow: buildCommonRowSnapshot(rows, mode, sourceAdj, mirrorAdj)}],
            },
            {
                fsId: companionFsId,
                code: companionCode,
                currency,
                adjustmentAmount: absAmount,
                entry: companionFields.entry,
                details: [{
                    [TAX_SALE_ATTR_ROW_REF_KEY]: {
                        rowId: mirrorRow?.id ?? mirrorRowIndex,
                        item: mirrorItem,
                        rowClass: TAX_COMMON_ROW_CLASS,
                        adjustment: mirrorAdj,
                    },
                }],
            },
        ],
    };
}

export type TaxSaleSyncResult = {
    updatedPlTaxDetail: Record<string, unknown>;
    mode: TaxPriorYearsSaleMode;
    sourceAdj: number;
    plTaxFields: ReturnType<typeof resolveFromPaperAdjustmentFields>;
    companionCode: TaxSaleCompanionCode;
    companionFields: {entry: 'Dr' | 'Cr'; adjustment: number};
};

/** 根据 PL-TAX commonRow 同步 mirror 行，并计算各节点非 DTD 的 adjustment / entry */
export function syncTaxSaleNodesFromPlTaxDetail(
    plTaxDetail: unknown,
    options?: {forTabsDialogEdit?: boolean},
): TaxSaleSyncResult | null {
    if (!plTaxDetail || typeof plTaxDetail !== 'object') return null;
    const detail = plTaxDetail as Record<string, unknown>;
    const rawRows = detail.commonRow;
    if (!Array.isArray(rawRows)) return null;

    const rows = rawRows.map((row) => ({...(row as CommonRow)}));
    const dataRowsOnly = rows.slice(0, TAX_CF_SOURCE_ROW_COUNT);

    let updatedRows = rows;
    if (!options?.forTabsDialogEdit) {
        const tablePending: Record<number, number> = {};
        for (const row of dataRowsOnly) {
            if (row?.id != null) {
                const adj = Number(row.adjustment) || 0;
                if (Math.abs(adj) > 0.001) {
                    tablePending[row.id] = adj;
                }
            }
        }

        const normalized = computeTaxSalePendingState({
            rows: dataRowsOnly,
            commonRow: rows,
            tablePending,
            isRowLocked: () => true,
            resolveAdjustmentSign: resolveTaxAdjustmentSign,
        });

        updatedRows = rows.map((row, idx) => {
            if (idx >= TAX_CF_SOURCE_ROW_COUNT) return row;
            if (row?.id != null && normalized[row.id] !== undefined) {
                return {...row, adjustment: normalized[row.id]};
            }
            return row;
        });
    }

    const mode = getTaxPriorYearsSaleMode(updatedRows, {}, () => false);
    if (!mode) return null;

    const sourceAdj = mode === 'under-provided'
        ? getUnderProvidedAdjustmentTotal(updatedRows, {}, () => false)
        : getOverProvidedAdjustmentTotal(updatedRows, {}, () => false);
    if (Math.abs(sourceAdj) < 0.001) return null;

    const companionCode: TaxSaleCompanionCode = mode === 'under-provided' ? 'BS-CA-TAXR' : 'BS-CL-TAXP';
    const plTaxOriginalEntry = getOriginalEntryFromFsCategory('PL-TAX', 'Dr');
    const plTaxFields = resolveFromPaperAdjustmentFields(plTaxOriginalEntry, sourceAdj, 'PL-TAX');
    const companionFields = resolveToPaperFieldsFromPairing('PL-TAX', sourceAdj, companionCode);

    return {
        updatedPlTaxDetail: {...detail, commonRow: updatedRows},
        mode,
        sourceAdj,
        plTaxFields,
        companionCode,
        companionFields,
    };
}

/** companion 节点编辑后，反推 PL-TAX 源行 adjustment */
export function updatePlTaxSourceRowFromCompanion(
    plTaxDetail: unknown,
    companionCode: TaxSaleCompanionCode,
    companionSignedAdj: number,
): Record<string, unknown> | null {
    if (!plTaxDetail || typeof plTaxDetail !== 'object') return null;
    const detail = plTaxDetail as Record<string, unknown>;
    const rawRows = detail.commonRow;
    if (!Array.isArray(rawRows)) return null;

    const mode: TaxPriorYearsSaleMode = companionCode === 'BS-CA-TAXR' ? 'under-provided' : 'over-provided';
    const absAmount = Math.abs(Number(companionSignedAdj) || 0);
    const sourceAdj = mode === 'over-provided' ? -absAmount : absAmount;
    const sourceRowIndex = mode === 'over-provided'
        ? TAX_OVER_PROVIDED_ROW_INDEX
        : TAX_UNDER_PROVIDED_ROW_INDEX;

    const updatedRows = rawRows.map((row, idx) => {
        const commonRow = row as CommonRow;
        const logicalIdx = taxLogicalRowIndex(commonRow, idx);
        if (logicalIdx === sourceRowIndex) {
            return {
                ...commonRow,
                adjustment: resolveTaxAdjustmentSign(logicalIdx, commonRow, sourceAdj),
            };
        }
        return commonRow;
    });

    return {...detail, commonRow: updatedRows};
}

/** getAdjustmentWithWorkingPapers 回显的 ADJUST_SALE：按 taxSaleAdjustment 结构组装提交 header */
export function buildTaxSaleHeaderFromDraft(params: {
    header: AuditAdjustHeader;
    plTaxDetail: unknown;
    getLineByCode: (code: string) => {fsId?: number; entry?: string | null} | null | undefined;
    preparedUserId: number;
    remark?: string;
}): AuditAdjustHeader | null {
    const detail = params.plTaxDetail as Record<string, unknown> | null | undefined;
    const rows = detail?.commonRow;
    if (!Array.isArray(rows)) return null;

    const commonRows = rows as CommonRow[];
    const mode = getTaxPriorYearsSaleMode(commonRows, {}, () => false);
    if (!mode) return null;

    const sourceAdj = mode === 'under-provided'
        ? getUnderProvidedAdjustmentTotal(commonRows, {}, () => false)
        : getOverProvidedAdjustmentTotal(commonRows, {}, () => false);
    if (Math.abs(sourceAdj) < 0.001) return null;

    const companionCode: TaxSaleCompanionCode = mode === 'under-provided' ? 'BS-CA-TAXR' : 'BS-CL-TAXP';
    const plTaxLine = params.getLineByCode('PL-TAX');
    const companionLine = params.getLineByCode(companionCode);
    if (!plTaxLine?.fsId || !companionLine?.fsId) return null;

    const plLineCurrency = params.header.lines?.find(
        (line) => String(line.code || '').trim().toUpperCase() === 'PL-TAX',
    )?.currency;

    const base = buildTaxPriorYearsSaleHeader({
        companyId: params.header.companyId,
        annualId: params.header.annualId,
        currency: plLineCurrency || 'HKD',
        preparedUserId: params.preparedUserId,
        rows: commonRows,
        mode,
        sourceAdj,
        plTaxFsId: plTaxLine.fsId,
        plTaxOriginalEntry: plTaxLine.entry,
        companionFsId: companionLine.fsId,
        companionCode,
        remark: params.remark ?? params.header.remark ?? 'To reclassify the accounts',
    });

    const mergeLineIds = (line: AuditAdjustLine): AuditAdjustLine => {
        const code = String(line.code || '').trim().toUpperCase();
        const existing = params.header.lines?.find(
            (existingLine) => String(existingLine.code || '').trim().toUpperCase() === code,
        );
        if (!existing) return line;
        return {
            ...line,
            id: existing.id,
            headerId: existing.headerId ?? params.header.id,
        };
    };

    return {
        ...base,
        id: params.header.id,
        refNo: params.header.refNo ?? '',
        status: params.header.status ?? 0,
        reviewedUserId: params.header.reviewedUserId ?? 0,
        reviewedDate: params.header.reviewedDate ?? '',
        lines: (base.lines || []).map(mergeLineIds),
    };
}

/** @deprecated Use buildTaxPriorYearsSaleHeader */
export function buildTaxOverProvidedSaleHeader(params: {
    companyId: number;
    annualId: number;
    currency: string;
    preparedUserId: number;
    rows: CommonRow[];
    overProvidedAdj: number;
    plTaxFsId: number;
    plTaxOriginalEntry?: string | null;
    bsClTaxpFsId: number;
    remark?: string;
}): AuditAdjustHeader {
    return buildTaxPriorYearsSaleHeader({
        ...params,
        mode: 'over-provided',
        sourceAdj: params.overProvidedAdj,
        companionFsId: params.bsClTaxpFsId,
        companionCode: 'BS-CL-TAXP',
    });
}

/**
 * MULTI non-first Tab: lastYearFinal ← prior Tab same-row currentFinal (perClient + adj).
 * Match by positional / logical index（含 c/f；item 名可能随符号变化）。
 */
export function applyTaxLastYearFinalFromPrior(
    currentDetail: any,
    priorDetail: any,
    opts?: {onlyIfEmpty?: boolean},
): any | null {
    if (!currentDetail || !priorDetail) return null;
    const onlyIfEmpty = opts?.onlyIfEmpty === true;
    const currRows: CommonRow[] = Array.isArray(currentDetail.commonRow) ? currentDetail.commonRow : [];
    const priorRows: CommonRow[] = Array.isArray(priorDetail.commonRow) ? priorDetail.commonRow : [];
    if (currRows.length === 0 || priorRows.length === 0) return null;

    const priorByLogical = new Map<number, CommonRow>();
    priorRows.forEach((row, idx) => {
        priorByLogical.set(taxLogicalRowIndex(row, idx), row);
    });

    let changed = false;
    const updated = currRows.map((row, idx) => {
        const logical = taxLogicalRowIndex(row, idx);
        const prior = priorByLogical.get(logical) ?? priorRows[idx];
        if (!prior) return row;
        const currLy = Number(row.lastYearFinal);
        if (onlyIfEmpty && Number.isFinite(currLy) && Math.abs(currLy) > 0.001) {
            return row;
        }
        const nextLy = computeWpRowCurrentFinal(prior);
        if (Number(row.lastYearFinal) === nextLy) return row;
        changed = true;
        return {...row, lastYearFinal: nextLy};
    });

    if (!changed) return null;
    return {...currentDetail, commonRow: updated};
}

/** Fingerprint of Tax commonRow for prior→lastYearFinal re-seed signatures. */
export function taxCommonRowFingerprint(detail: any): string {
    const rows: CommonRow[] = Array.isArray(detail?.commonRow) ? detail.commonRow : [];
    return rows.map((row, idx) => {
        const logical = taxLogicalRowIndex(row, idx);
        const pc = Number(row?.perClient) || 0;
        const adj = getAdjustmentTotal(row);
        const ly = Number(row?.lastYearFinal) || 0;
        return `${logical}:${pc}:${adj}:${ly}`;
    }).join('|');
}

/**
 * MULTI inactive-tab Final for Tax b/f: persisted perClient is often 0 (FS/roll display overlay).
 * When empty, use signed lastYearFinal as the display Per client (non-first Tab split-left).
 */
function taxDetailDisplayPerClient(row: CommonRow, logicalIndex: number): number {
    const pc = Number(row.perClient) || 0;
    if (logicalIndex !== TAX_REFUNDABLE_BF_ROW_INDEX && logicalIndex !== TAX_PAYABLE_BF_ROW_INDEX) {
        return pc;
    }
    if (Math.abs(pc) > 0.001) return pc;
    const ly = Number(row.lastYearFinal) || 0;
    if (logicalIndex === TAX_REFUNDABLE_BF_ROW_INDEX) return ly === 0 ? 0 : -Math.abs(ly);
    if (logicalIndex === TAX_PAYABLE_BF_ROW_INDEX) return ly === 0 ? 0 : Math.abs(ly);
    return pc;
}

function taxDetailRowCurrentFinal(row: CommonRow, logicalIndex: number): number {
    return taxDetailDisplayPerClient(row, logicalIndex) + getAdjustmentTotal(row);
}

/** MULTI WpPerCheck: sum Final for logical rows 0–6 (excludes c/f). */
export function sumTaxDetailRows0To6Final(detail: any): number {
    const rows: CommonRow[] = Array.isArray(detail?.commonRow) ? detail.commonRow : [];
    let total = 0;
    for (let idx = 0; idx < rows.length; idx++) {
        const row = rows[idx];
        const logical = taxLogicalRowIndex(row, idx);
        if (logical < 0 || logical >= TAX_CF_SOURCE_ROW_COUNT) continue;
        total += taxDetailRowCurrentFinal(row, logical);
    }
    return total;
}

/**
 * MULTI WpPerCheck Tax Payable lastYear: first-tab c/f lastYearFinal.
 * c/f is display-only (not persisted); fall back to FS mirror when missing.
 */
export function getTaxDetailCfLastYearFinal(detail: any, fsMirrorCfLastYear: number): number {
    const rows: CommonRow[] = Array.isArray(detail?.commonRow) ? detail.commonRow : [];
    const cfRow = rows.find((row, idx) => taxLogicalRowIndex(row, idx) === TAX_CF_ROW_INDEX)
        ?? rows[TAX_CF_ROW_INDEX];
    if (cfRow && cfRow.lastYearFinal != null) {
        return Number(cfRow.lastYearFinal) || 0;
    }
    return Number(fsMirrorCfLastYear) || 0;
}

const TAXATION_AMOUNT_LOGICAL_INDICES = [
    TAX_EXPENSES_ROW_INDEX,
    TAX_OVER_PROVIDED_ROW_INDEX,
    TAX_UNDER_PROVIDED_ROW_INDEX,
] as const;

/** MULTI WpPerCheck Taxation Amount current: rows 4+5+6 Final. */
export function sumTaxDetailTaxationAmountCurrent(detail: any): number {
    const rows: CommonRow[] = Array.isArray(detail?.commonRow) ? detail.commonRow : [];
    let total = 0;
    for (const logicalIdx of TAXATION_AMOUNT_LOGICAL_INDICES) {
        const row = rows.find((r, idx) => taxLogicalRowIndex(r, idx) === logicalIdx)
            ?? rows[logicalIdx];
        if (!row) continue;
        total += taxDetailRowCurrentFinal(row, logicalIdx);
    }
    return total;
}

/** MULTI WpPerCheck Taxation Amount lastYear: rows 4+5+6 lastYearFinal. */
export function sumTaxDetailTaxationAmountLastYear(detail: any): number {
    const rows: CommonRow[] = Array.isArray(detail?.commonRow) ? detail.commonRow : [];
    let total = 0;
    for (const logicalIdx of TAXATION_AMOUNT_LOGICAL_INDICES) {
        const row = rows.find((r, idx) => taxLogicalRowIndex(r, idx) === logicalIdx)
            ?? rows[logicalIdx];
        if (!row) continue;
        total += Number(row.lastYearFinal) || 0;
    }
    return total;
}
